import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Menu, SubMenu, Module } from "../../../models/menu/menu.model";
import { getUrlPathFromComponentPath } from "./routeMapping";
import { MenuRoutes } from "./routes";
import { SearchResult, UserMenuProps } from "./types";
import { ModuleType } from "../../../types/enum";
import AuthService from "../../../services/autentication/auth.service";
import RouteConstant from "../../../constants/route.constants";
import { getMenuDisplayLabel } from "./displayLabel";
import "./menu.css";

const showMenuIcon = (item: Pick<SubMenu, "menuName" | "icon">) =>
  !!item.icon && !["DeliveryAssessmentPlanning", "PincodePOSMapping"].includes(item.menuName);

/**
 * UserMenu Component
 * A dynamic, searchable menu system that displays modules, menus, and submenus
 * fetched from the API with permissions-based filtering.
 * Note: MyAccount module is excluded as it's handled by ProfileMenu
 */
const UserMenu: React.FC<UserMenuProps> = ({
  className,
  isOpen: externalIsOpen,
  onOpenChange,
  activeModuleId: externalActiveModuleId,
  onActiveModuleChange,
}) => {
  const { menuData: allModules, loading, error } = MenuRoutes();

  // Filter out MyAccount module as it's handled separately by ProfileMenu
  const modules = useMemo(
    () => allModules.filter(m => m.moduleId !== ModuleType.MyAccount),
    [allModules]
  );

  const [internalActiveModuleId, setInternalActiveModuleId] = useState<number | null>(null);
  const [internalIsOpen, setInternalIsOpen] = useState(false);

  // Use external state if provided, otherwise use internal state
  const isOpen = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen;
  const activeModuleId = externalActiveModuleId !== undefined ? externalActiveModuleId : internalActiveModuleId;

  const setIsOpen = (value: boolean | ((prev: boolean) => boolean)) => {
    const newValue = typeof value === 'function' ? value(isOpen) : value;
    if (onOpenChange) {
      onOpenChange(newValue);
    } else {
      setInternalIsOpen(newValue);
    }
  };

  const setActiveModuleId = (value: number | null) => {
    if (onActiveModuleChange) {
      onActiveModuleChange(value);
    } else {
      setInternalActiveModuleId(value);
    }
  };
  const [searchQuery, setSearchQuery] = useState("");
  const [isMinimized, setIsMinimized] = useState(false);
  const [collapsedMenus, setCollapsedMenus] = useState<Set<string>>(new Set());
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);

  const menuRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Sort modules once and memoize
  const sortedModules = useMemo(
    () => [...modules].sort((a, b) => a.displayOrder - b.displayOrder),
    [modules]
  );

  // Get active module
  const activeModule = useMemo(
    () => modules.find((m) => m.moduleId === activeModuleId),
    [modules, activeModuleId]
  );


  // Handle scroll indicator
  useEffect(() => {
    const contentElement = contentRef.current;
    if (!contentElement || !isOpen) return;

    const checkScrollable = () => {
      const { scrollHeight, clientHeight, scrollTop } = contentElement;
      const hasMoreContent = scrollHeight > clientHeight;
      const isAtBottom = scrollHeight - scrollTop - clientHeight < 10;
      setShowScrollIndicator(hasMoreContent && !isAtBottom);
    };

    checkScrollable();
    contentElement.addEventListener("scroll", checkScrollable);
    window.addEventListener("resize", checkScrollable);

    return () => {
      contentElement.removeEventListener("scroll", checkScrollable);
      window.removeEventListener("resize", checkScrollable);
    };
  }, [isOpen, searchQuery, collapsedMenus, activeModuleId]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setCollapsedMenus(new Set()); // Reset collapsed menus when closing
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Initialize active module when modules load
  useEffect(() => {
    if (modules.length > 0 && activeModuleId === null) {
      setActiveModuleId(modules[0].moduleId);
    } else if (modules.length === 0 && activeModuleId !== null) {
      setActiveModuleId(null);
    }
  }, [modules, activeModuleId]);

  // Auto-minimize when there's only one module
  useEffect(() => {
    if (sortedModules.length === 1 && activeModule) {
      setIsMinimized(true);
    }
  }, [sortedModules.length, activeModule]);

  // Event handlers
  const handleMenuClick = useCallback(() => {
    setIsOpen((prev) => !prev);
    if (!isOpen) {
      setSearchQuery("");
      setCollapsedMenus(new Set()); // Reset collapsed menus when opening
    }
  }, [isOpen]);

  const handleModuleClick = useCallback((moduleId: number) => {
    setActiveModuleId(moduleId);
  }, []);

  const toggleMenuCollapse = useCallback((uniqueKey: string) => {
    setCollapsedMenus((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(uniqueKey)) {
        newSet.delete(uniqueKey);
      } else {
        newSet.add(uniqueKey);
      }
      return newSet;
    });
  }, []);

  const handleMenuItemClick = useCallback(
    (route: string | null, displayName?: string) => {
      if (!route) return;

      const urlPath = getUrlPathFromComponentPath(route, displayName);
      navigate(urlPath);
      setIsOpen(false);
      setSearchQuery("");
    },
    [navigate]
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value);
    },
    []
  );

  const clearSearch = useCallback(() => {
    setSearchQuery("");
  }, []);

  const toggleMinimize = useCallback(() => {
    setIsMinimized((prev) => !prev);
  }, []);

  // Search across all modules
  const searchResults = useMemo((): SearchResult[] => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return [];

    const results: SearchResult[] = [];

    modules.forEach((module) => {
      module.menus.forEach((menu) => {
        menu.subMenu?.forEach((item) => {
          const itemLabel = getMenuDisplayLabel(item.displayName);
          if (
            item.permissions.canView &&
            (item.displayName.toLowerCase().includes(query) ||
              itemLabel.toLowerCase().includes(query) ||
              item.route?.toLowerCase().includes(query))
          ) {
            results.push({
              item,
              menuName: menu.displayName,
              moduleName: module.displayName,
            });
          }
        });
      });
    });

    return results;
  }, [modules, searchQuery]);

  // Get filtered menus for current module
  const filteredMenus = useMemo((): Menu[] => {
    if (!searchQuery.trim()) {
      return activeModule?.menus || [];
    }

    const query = searchQuery.toLowerCase();
    const allMenus: Menu[] = [];

    modules.forEach((module) => {
      module.menus.forEach((menu) => {
        const menuNameMatches = menu.displayName.toLowerCase().includes(query);
        const filteredSubMenus = menu.subMenu?.filter(
          (item) =>
            item.permissions.canView &&
            (item.displayName.toLowerCase().includes(query) ||
              item.route?.toLowerCase().includes(query))
        ) || [];

        if (menuNameMatches || filteredSubMenus.length > 0) {
          allMenus.push({
            ...menu,
            subMenu: menuNameMatches ? menu.subMenu : filteredSubMenus,
          });
        }
      });
    });

    return allMenus;
  }, [modules, activeModule, searchQuery]);

  // Render menu items
  const renderMenuItems = useCallback(
    (items: SubMenu[]): JSX.Element[] => {
      return items
        .filter((item) => item.permissions.canView)
        .sort((a, b) => a.displayOrder - b.displayOrder)
        .map((item) => (
          <div
            key={item.menuId}
            className="menu-item"
            onClick={() => handleMenuItemClick(item.route, item.displayName)}
          >
            {showMenuIcon(item) && <i className={item.icon}></i>}
            <span>{getMenuDisplayLabel(item.displayName)}</span>
          </div>
        ));
    },
    [handleMenuItemClick]
  );

  // Check for authentication
  const isUserAuthenticated = AuthService.isUserLoggedIn();

  // Only return null (hide menu) if authenticated but we have no data/error.
  // If unauthenticated, we proceed to render checking !isUserAuthenticated inside.
  if (isUserAuthenticated && (loading || error || modules.length === 0)) {
    return null;
  }

  return (
    <div className={`menu-container ${className || ""}`} ref={menuRef}>
      <button
        type="button"
        className={`topbar-icon-btn topbar-menu-btn menu-trigger ${isOpen ? "active" : ""}`}
        onClick={handleMenuClick}
      >
        <i className="pi pi-bars"></i>
      </button>

      {isOpen && (
        <div className={`menu-dropdown ${isMinimized ? "minimized" : ""}`}>

          {!isUserAuthenticated ? (
            /* Unauthenticated State */
            <div className="flex flex-column align-items-center justify-content-center p-4 gap-3 text-center" style={{ height: '100%' }}>
              <i className="pi pi-exclamation-circle text-5xl text-yellow-500"></i>
              <div>
                <span className="font-bold text-xl block mb-2">Session Expired</span>
                <p className="m-0 text-gray-600">Please login to access the menu.</p>
              </div>
              <button
                className="p-button p-component p-button-primary"
                onClick={() => {
                  setIsOpen(false);
                  navigate(RouteConstant.login);
                }}
              >
                <span className="p-button-icon p-c p-button-icon-left pi pi-sign-in"></span>
                <span className="p-button-label p-c">Login</span>
              </button>
            </div>
          ) : (
            /* Authenticated Content */
            <>
              {/* Sticky Header with Search and Controls */}
              <div className="menu-sticky-header">
                <div className="menu-header">
                  {/* Search Bar */}
                  <div className="menu-search">
                    <i className="pi pi-search menu-search-icon"></i>
                    <input
                      type="text"
                      className="menu-search-input"
                      placeholder="Search across all menus..."
                      value={searchQuery}
                      onChange={handleSearchChange}
                      autoComplete="off"
                    />
                    {searchQuery && (
                      <button
                        className="menu-search-clear"
                        onClick={clearSearch}
                        type="button"
                      >
                        <i className="pi pi-times"></i>
                      </button>
                    )}
                  </div>

                  {/* Minimize/Maximize Button */}
                  <button
                    className="menu-toggle-size"
                    onClick={toggleMinimize}
                    type="button"
                    title={isMinimized ? "Maximize" : "Minimize"}
                  >
                    <i className={`pi ${isMinimized ? "pi-window-maximize" : "pi-window-minimize"}`}></i>
                  </button>
                  {/* Close Button */}
                  <button
                    className="menu-close-btn"
                    onClick={() => {
                      setIsOpen(false);
                      setCollapsedMenus(new Set()); // Reset collapsed menus when closing
                    }}
                    type="button"
                    title="Close"
                  >
                    <i className="pi pi-times"></i>
                  </button>
                </div>

                {/* Module Tabs - hide if only one module */}
                {sortedModules.length > 1 && (
                  <div className="menu-tabs">
                    {sortedModules.map((module) => (
                      <button
                        key={module.moduleId}
                        className={`menu-tab ${activeModuleId === module.moduleId ? "active" : ""}`}
                        onClick={() => handleModuleClick(module.moduleId)}
                      >
                        {module.icon && <i className={module.icon}></i>} &nbsp;
                        <span>{module.displayName === "Raw Material Purchase" ? "Purchases" : module.displayName}</span>
                      </button>
                    ))}
                  </div>
                )}

                {/* Module Name as Main Header */}
                {activeModule && sortedModules.length === 1 && (
                  <div className="menu-module-header">
                    {activeModule.icon && <i className={activeModule.icon}></i>}
                    <h2>{activeModule.displayName}</h2>
                  </div>
                )}
              </div>

              {/* Scrollable Content */}
              <div className="menu-content" ref={contentRef}>
                {searchQuery.trim() ? (
                  /* Search Results View */
                  <div className="menu-search-results">
                    {searchResults.length === 0 ? (
                      <div className="menu-no-results">
                        <i className="pi pi-search"></i>
                        <span>No menus found for "{searchQuery}"</span>
                      </div>
                    ) : (
                      searchResults.map((result, index) => (
                        <div
                          key={`${result.item.menuId}-${index}`}
                          className="menu-search-result-item"
                          onClick={() => handleMenuItemClick(result.item.route, result.item.displayName)}
                        >
                          <div className="menu-search-result-title">
                            {showMenuIcon(result.item) && <i className={result.item.icon}></i>}
                            <span>{getMenuDisplayLabel(result.item.displayName)}</span>
                          </div>
                          <div className="menu-search-result-meta">
                            {result.moduleName} • {result.menuName}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                ) : (
                  /* Normal Menu Grid View */
                  <div className="menu-grid">
                    {filteredMenus.map((menu, index) => {
                      const hasSubMenus = menu.subMenu && menu.subMenu.length > 0;
                      const uniqueKey = `${menu.menuId}_${index}`;
                      const isCollapsed = collapsedMenus.has(uniqueKey);

                      return (
                        <div key={uniqueKey} className={`menu-section ${isCollapsed ? "collapsed" : ""}`}>
                          {/* Menu Name as Sub-Header - clickable to toggle */}
                          <h3
                            className="menu-section-header collapsible"
                            onClick={() => toggleMenuCollapse(uniqueKey)}
                          >
                            <div className="menu-section-header-content">
                              {showMenuIcon(menu) && <i className={menu.icon}></i>}
                              <span>{getMenuDisplayLabel(menu.displayName)}</span>
                            </div>
                            <i className={`pi ${isCollapsed ? "pi-chevron-down" : "pi-chevron-up"} menu-collapse-icon`}></i>
                          </h3>

                          {/* Submenu Items List */}
                          {!isCollapsed && (
                            <div className="menu-items-list">
                              {hasSubMenus ? (
                                renderMenuItems(menu.subMenu)
                              ) : (
                                <div
                                  className="menu-item"
                                  onClick={() => handleMenuItemClick(menu.route, menu.displayName)}
                                >
                                  {showMenuIcon(menu) && <i className={menu.icon}></i>}
                                  <span>{getMenuDisplayLabel(menu.displayName)}</span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Scroll Indicator */}
              {showScrollIndicator && (
                <div className="menu-scroll-indicator">
                  <i className="pi pi-chevron-down"></i>
                  <span>Scroll for more</span>
                </div>
              )}
            </>
          )}

        </div>
      )}
    </div>
  );
};

export default UserMenu;
